
import { VEmojiPicker } from 'v-emoji-picker';
import MediaBar from '@/components/ui/molecule/MediaBar';
import PollCreate from '@/components/ui/molecule/PollCreate';
import MultipleImageDisplay from '@/components/ui/molecule/MultipleImageDisplay';
import PostBox from '@/components/ui/molecule/PostBox';
import { PostDisabledReason } from '~/types/models/post';
import { getMedia } from '@/utils/mixins';
import UIPost from '@/components/ui/organism/UIPost';
import { buildAssetUri, isMobileResponsive } from '@/utils/mixins';
import { mapActions } from 'vuex';
import CloseIcon from '~/components/ui/icons/CloseIcon.vue';
import UITransactionPost from '../molecule/UITransactionPost.vue';
import GifSelectorDrawer from '../molecule/GifSelectorDrawer.vue';
import GifSelectorModal from '../molecule/GifSelectorModal.vue';
import { GifTypes } from '~/types/models/gifTypes';
import OverflowContainer from '../molecule/OverflowContainer.vue';
import SharingOptionPill from '../molecule/SharingOptionPill.vue';
import PortfolioIcon from '../icons/PortfolioIcon.vue';
import MarketIcon from '../icons/MarketIcon.vue';
import LearnIcon from '../icons/LearnIcon.vue';
import PostPollIcon from '../icons/PostPollIcon.vue';
import AcademyGuidesSearchModal from '../molecule/AcademyGuidesSearchModal.vue';
import UIPostGuideCard from '../molecule/UIPostGuideCard.vue';
import TickerSearchModal from '../molecule/TickerSearchModal.vue';
import UIPostTickerList from './UIPostTickerList.vue';
import PortfolioCardWithDonutChart from '../molecule/PortfolioCardWithDonutChart.vue';
import { ImageFormat } from '~/utils/behavior/imageUrl';
import PostLeagueCard from '../molecule/PostLeagueCard.vue';
import { PortfolioTransactionTypes } from '~/utils/constants/portfolioConstants';

const GIF_TYPE_MAP = [
  { match: 'klipy.com', type: GifTypes.KLIPY },
  { match: 'giphy.com', type: GifTypes.GIPHY },
];

export default {
  name: 'UICreatePost',
  components: {
    VEmojiPicker,
    MediaBar,
    GifSelectorModal,
    PollCreate,
    MultipleImageDisplay,
    PostBox,
    UIPost,
    CloseIcon,
    UITransactionPost,
    GifSelectorDrawer,
    OverflowContainer,
    SharingOptionPill,
    PortfolioIcon,
    MarketIcon,
    LearnIcon,
    PostPollIcon,
    AcademyGuidesSearchModal,
    UIPostGuideCard,
    TickerSearchModal,
    UIPostTickerList,
    PortfolioCardWithDonutChart,
    PostLeagueCard,
  },
  mixins: [getMedia, buildAssetUri, isMobileResponsive],

  props: {
    searchMentions: {
      type: Function,
      required: true,
    },
    searchHashtags: {
      type: Function,
      required: true,
    },
    placeholder: {
      type: String,
      required: true,
    },
    showEditIcon: {
      type: Boolean,
      default: true,
    },
    clear: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    uploadImage: {
      type: Function,
      required: true,
    },
    targetType: {
      type: String,
      required: true,
    },
    target: {
      type: String,
      required: true,
    },
    mentionedPost: {
      type: Object,
      default: () => null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Used to provide a way for external components to submit the post
     */
    submitFromExternal: {
      type: Boolean,
      default: false,
    },
    /**
     * If the submit comes from some external component, use this to hide the default submit button
     */
    showSubmitBtn: {
      type: Boolean,
      default: true,
    },
    /**
     * Used to provide a way for external components to edit the post
     */
    editFromExternal: {
      type: Boolean,
      default: false,
    },
    /**
     * If the submit comes from some external component, use this to hide the default submit button
     */
    showEditBtn: {
      type: Boolean,
      default: true,
    },
    showMediaBar: {
      type: Boolean,
      default: true,
    },
    focusEditorAtCreation: {
      type: Boolean,
      default: false,
    },
    /**
     * Use this as a replacement for focusAtCreation when the component is shown rather than created
     */
    focusEditor: {
      type: Boolean,
      default: false,
    },
    profileAvatarUri: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    hasBackground: {
      type: Boolean,
      default: false,
    },
    hasFocusBorder: {
      type: Boolean,
      default: false,
    },
    hasPadding: { type: Boolean, default: true },
    portfolio: {
      type: Object,
      default: () => null,
    },
    post: {
      type: Object,
      required: false,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    openEditorPreventsScroll: {
      type: Boolean,
      default: true,
    },
    isMobileReply: {
      type: Boolean,
      default: false,
    },
    transactionData: {
      type: Object,
      default: () => null,
      required: false,
    },
    isDesktopModal: {
      type: Boolean,
      default: false,
    },
    showPostBoxYPadding: {
      type: String,
      default: '2',
    },
    customBoxPadding: {
      type: String,
      default: '0',
    },
    isEmailConfirmed: {
      type: Boolean,
      default: false,
    },
    fullWidthTextbox: {
      type: Boolean,
      default: true,
    },
    league: {
      type: Object,
      default: null,
    },
    roundedBorder: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      editor: null,
      currentMedia: '',
      imagesToUpload: null,
      gifToUpload: null,
      currentEditorIndex: null,
      disabledByContent: false,
      disabledTooltip: null,
      embed: null,
      postBoxKey: 0,
      pollId: null,
      //new flag that will be used to prevent multiple display of prefill text
      //if it is true, prefill text will not be displayed over new text that user enters
      //if it is false, that means that editor is reset, and prefill text will be displayed
      editorInitialized: false,
      editorVisible: false,
      dynamicPlaceholder: this.placeholder,
      pollPayload: null,
      postButtonState: false,
      selectedSharingOption: '',
      selectedGuide: null,
      guideRemoved: false,
      selectedTickers: [],
      tickerDataLoading: false,
      tickersInPostLimit: 5,
      imageFormats: ImageFormat,
      isEditorEmpty: true,
      transactionTypes: PortfolioTransactionTypes,
    };
  },
  computed: {
    imagesToDisplay() {
      if (this.gifToUpload) {
        return [{ src: this.gifToUpload.uri }];
      } else if (this.imagesToUpload) {
        return this.imagesToUpload.map((image) => ({
          src: image.preview,
        }));
      } else {
        return null;
      }
    },

    disabledMedia() {
      if (this.gifToUpload) {
        return ['image', 'bold', 'italic', 'underline', 'bullet', 'poll'];
      } else if (this.imagesToUpload && this.imagesToUpload.length) {
        return ['gif', 'bold', 'italic', 'underline', 'bullet', 'poll'];
      }
      if (this.disabled) {
        return [
          'image',
          'gif',
          'emoji',
          'bold',
          'italic',
          'underline',
          'bullet',
          'poll',
        ];
      }
      return [];
    },
    displayedMedia() {
      let res = ['gif', 'bold', 'italic', 'underline', 'bullet'];
      if (!this.isMobileReply) res.push('poll');
      // Removing emojis and images in replies on mobile for now
      if (!this.isMobileResponsive) {
        res = [...res, 'emoji'];
      }
      if (
        !this.isMobileResponsive ||
        this.targetType != 'POST' ||
        this.isMobileReply
      ) {
        res = [...res, 'image'];
      }
      return res;
    },
    showTransactionData() {
      return !!this.transactionData;
    },
    prefillText() {
      if (!this.showTransactionData) return '';

      const textMap = {
        [this.transactionTypes.TRANSACTION]: 'post_transaction_shared_text',
        [this.transactionTypes.ORDER]: 'post_order_shared_text',
      };

      const translationKey = textMap[this.transactionData.transactionType];

      return translationKey ? this.$t(translationKey) : '';
    },
    showGuideCard() {
      return !!this.guideCardData;
    },
    showSharingOptions() {
      return (
        this.selectedSharingOption === '' &&
        !this.showGuideCard &&
        !this.showTickerCards &&
        !this.portfolio
      );
    },
    isPollActive() {
      return this.selectedSharingOption === 'poll';
    },
    isRepost() {
      return this.mentionedPost !== null;
    },
    hasCustomBoxPadding() {
      return this.customBoxPadding !== '0';
    },
    guideCardData() {
      if (this.selectedGuide) return this.selectedGuide;
      if (this.guideRemoved) return null;
      return this.post?.academyGuideDetails || null;
    },
    sharingOptions() {
      const options = [
        {
          key: 'poll',
          label: this.$t('poll'),
          icon: 'PostPollIcon',
        },
        // Hiding these options until they are done
        // {
        //   key: 'portfolio',
        //   label: this.$t('portfolio'),
        //   icon: 'PortfolioIcon',
        // },
        {
          key: 'ticker',
          label: this.$t('ticker_sharing_option'),
          icon: 'MarketIcon',
        },
        {
          key: 'guide',
          label: this.$t('guide'),
          icon: 'LearnIcon',
        },
      ];

      if (this.isEdit) {
        return options.filter((option) => option.key !== 'poll');
      }

      return options;
    },
    tickersData() {
      return this.selectedTickers;
    },
    showTickerCards() {
      return this.tickersData.length > 0 || this.tickerDataLoading;
    },
    showAddMoreTickersButton() {
      return (
        this.tickersData.length > 0 &&
        this.tickersData.length < this.tickersInPostLimit
      );
    },
    isPostEmpty() {
      return (
        this.isEditorEmpty &&
        !this.gifToUpload &&
        !this.imagesToUpload?.length &&
        !this.selectedTickers.length &&
        !this.selectedGuide &&
        !this.isPollActive
      );
    },
  },
  watch: {
    clear(newVal) {
      if (newVal && this.editor) {
        this.imagesToUpload = null;
        this.gifToUpload = null;
        this.currentMedia = '';
        this.isEditorEmpty = true;

        this.editor.setText('');
        this.editorInitialized = !!this.prefillText;

        this.resetPostBox();
      }
    },
    submitFromExternal() {
      if (this.submitFromExternal) {
        this.submitPost();
      }
    },
    editFromExternal() {
      if (this.editFromExternal) {
        this.updatePost();
      }
    },
    currentMedia() {
      if (this.editor) {
        const newPlaceholder = this.dynamicPlaceholder;
        this.editor.root.dataset.placeholder = newPlaceholder;
        this.editor.focus({ preventScroll: true });
      }
      this.$emit('onCurrentMediaChange', this.isPollActive);
    },
    post: {
      immediate: true,
      handler(newPost) {
        if (newPost) {
          this.setMediaForPostUpdate();
          if (this.isEdit) {
            this.selectedTickers = [...(newPost.tickers || [])];
          }
        }
      },
    },
    isPostEmpty: {
      // Running in immediate mode to ensure that the initial state of isPostEmpty is emitted when the component is mounted
      // Previously this was creating bugs for posts with gifs or tickers
      immediate: true,
      handler(val) {
        this.$emit('isPostEmptyChange', val);
      },
    },
  },
  methods: {
    ...mapActions('api', [
      'getPostImageForPreview',
      'getCompanySymbolsV2',
      'getSymbolProfileById',
    ]),
    ...mapActions('post', ['createPoll']),
    resetPostBox() {
      this.postBoxKey += 1;
    },
    toggleDisable(disable) {
      this.disabledByContent = disable.disabled;
      switch (disable.reason) {
        case PostDisabledReason.CONTENT:
          this.disabledTooltip = this.$t('post_disabled_bad_content');
          break;
        case PostDisabledReason.LENGTH:
          this.disabledTooltip = this.$t('post_disabled_length');
          break;
        default:
          return;
      }
    },
    getGifType() {
      const uri = this.gifToUpload?.uri;
      if (!uri) {
        return null;
      }

      const match = GIF_TYPE_MAP.find((item) => uri.includes(item.match));

      return match ? match.type : null;
    },

    async submitPost() {
      const images = this.getImageFormData();
      const mentions = this.getMentions();
      const body = this.getBody(mentions);
      const customFormat = this.getCustomFormat();
      const gifType = this.getGifType();

      let pollId = null;
      if (this.isPollActive && this.pollPayload) {
        const poll = await this.createPoll(this.pollPayload);
        pollId = poll[0].pollId;
      }

      if (
        body ||
        images ||
        this.gifToUpload ||
        mentions.length ||
        pollId ||
        this.selectedTickers.length ||
        this.selectedGuide
      ) {
        this.$emit('onSubmit', {
          body: body,
          mentions,
          gifUri: this.gifToUpload ? this.gifToUpload.uri : null,
          gifType,
          images,
          targetType: this.targetType,
          target: this.target,
          embed: this.embed,
          pollId: pollId,
          portfolioId: this.portfolio?.id,
          customFormat: customFormat,
          transaction: this.transactionData ? this.transactionData : null,
          academyGuideId: this.selectedGuide?.academyGuideId,
          tickers: this.selectedTickers,
          leagueId: this.league?.leagueId,
        });
        this.dynamicPlaceholder = '';
        this.pollPayload = null;
        this.selectedGuide = null;
        this.selectedSharingOption = '';
        this.selectedTickers = [];
        this.resetPostBox();
      }
    },
    updatePost() {
      const images = this.getImageFormData();
      const mentions = this.getMentions();
      const body = this.getBody(mentions);
      const customFormat = this.getCustomFormat();
      const gifType = this.getGifType();

      if (
        body ||
        mentions.length ||
        this.selectedTickers.length ||
        this.selectedGuide
      ) {
        this.$emit('onUpdate', {
          postId: this.post.postId,
          body: body,
          mentions,
          gifUri: this.gifToUpload ? this.gifToUpload.uri : null,
          gifType,
          images,
          embed: this.embed,
          targetType: this.post.targetType,
          target: this.post.target,
          replyCount: this.post.replyCount,
          replies: this.post.replies,
          customFormat: customFormat,
          academyGuideId: this.selectedGuide?.academyGuideId,
          tickers: this.selectedTickers,
          leagueId: this.league?.leagueId,
        });
      }
    },
    getBody(mentions = []) {
      let body = this.editor
        .getContents()
        .ops.map((op) => {
          if (typeof op.insert === 'string') {
            return op.insert;
          }
          if (op.insert.mention) {
            return op.insert.mention.denotationChar + op.insert.mention.value;
          }
        })
        .join('')
        .replace(/\n$/, '');

      mentions.forEach((mention) => {
        if (mention.type === '@') {
          const fullMention = mention.type + mention.value;
          const escaped = fullMention.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          body = body.replace(
            new RegExp(escaped, 'g'),
            `${mention.type}${mention.target}`
          );
        }
      });
      return body;
    },
    getCustomFormat() {
      const delta = this.editor.getContents().ops;
      const boldRanges = [];
      const bulletRanges = [];
      const italicRanges = [];
      const underlineRanges = [];
      let index = 0;

      delta.forEach((op) => {
        const textLength =
          typeof op.insert === 'string'
            ? op.insert.length
            : op.insert.mention.denotationChar === '@'
            ? op.insert.mention.id.length + 1 // Increasing the textLenght by 1 compensates for mention or tag prefix
            : op.insert.mention.value.length + 1;

        if (
          op.attributes?.bold &&
          (op.insert?.mention || op.insert.trim() !== '')
        ) {
          boldRanges.push({ start: index, end: index + textLength });
        }

        if (
          op.attributes?.italic &&
          (op.insert?.mention || op.insert.trim() !== '')
        ) {
          italicRanges.push({ start: index, end: index + textLength });
        }

        if (
          op.attributes?.underline &&
          (op.insert?.mention || op.insert.trim() !== '')
        ) {
          underlineRanges.push({ start: index, end: index + textLength });
        }

        if (op.attributes?.list === 'bullet') {
          bulletRanges.push({
            start: index - 1,
            end: index + textLength - 1,
            type: 'bullet',
          });
        }

        index += textLength;
      });

      return boldRanges.length ||
        italicRanges.length ||
        underlineRanges.length ||
        bulletRanges.length
        ? JSON.stringify({
            ...(boldRanges.length && { bold: boldRanges }),
            ...(italicRanges.length && { italic: italicRanges }),
            ...(underlineRanges.length && { underline: underlineRanges }),
            ...(bulletRanges.length && { list: bulletRanges }),
          })
        : null;
    },
    getMentions() {
      const contents = this.editor.getContents();
      const res = [];

      contents.forEach((e) => {
        if (e.insert && e.insert.mention) {
          res.push({
            type: e.insert.mention.denotationChar,
            target: e.insert.mention.id,
            value: e.insert.mention.value,
          });
        }
      });

      if (this.mentionedPost) {
        res.push({ type: 'POST', target: this.mentionedPost.postId });
      }
      return res;
    },
    getImageFormData() {
      if (this.imagesToUpload && this.imagesToUpload.length) {
        return this.imagesToUpload.map((e) => e.id);
      } else {
        return null;
      }
    },
    async handleImage(file) {
      if ((this.imagesToUpload || []).length + 1 > 4) {
        this.$notify.error(this.$t('max_image_to_upload'));
      } else {
        file = await this.uploadImage(file);
        this.imagesToUpload = [...(this.imagesToUpload || []), file];
      }
    },
    removeImage(index) {
      if (this.gifToUpload) {
        this.gifToUpload = null;
      } else {
        this.$emit('removeImage', this.imagesToUpload[index].id);
        this.imagesToUpload.splice(index, 1);
      }
    },
    handleSelectMedia(selectedMedia) {
      const format = this.editor.getFormat();
      if (selectedMedia === 'bold') {
        const isBold = !!format.bold;
        this.editor.format('bold', !isBold);
        this.currentMedia = isBold ? '' : 'bold';
      } else if (selectedMedia === 'italic') {
        const isItalic = !!format.italic;
        this.editor.format('italic', !isItalic);
        this.currentMedia = isItalic ? '' : 'italic';
      } else if (selectedMedia === 'underline') {
        const isUnderline = !!format.underline;
        this.editor.format('underline', !isUnderline);
        this.currentMedia = isUnderline ? '' : 'underline';
      } else if (selectedMedia === 'bullet') {
        const isBullet = !!format.list && format.list === 'bullet';
        this.editor.format('list', isBullet ? false : 'bullet');
        this.currentMedia = isBullet ? '' : 'bullet';
      } else if (this.currentMedia === selectedMedia) {
        this.currentMedia = '';
      } else {
        this.currentMedia = selectedMedia;
      }
    },
    selectEmoji(emoji) {
      const tmpIndex = this.currentEditorIndex || 0;
      this.editor.insertText(this.currentEditorIndex || 0, emoji.data, 'user');
      this.currentEditorIndex = tmpIndex + emoji.data.length;
      this.currentMedia = '';
    },
    handleClickOutsideEmojiPicker() {
      this.currentMedia = '';
    },
    onClickGIF(gif) {
      this.gifToUpload = gif;
      this.currentMedia = '';
    },
    calcIndexWithMentions() {
      const textLength =
        this.editor.getText().length <= 0
          ? 0
          : this.editor.getText().length - 1;
      return textLength + this.getMentionsLength();
    },
    getEditor(editor) {
      this.editor = editor;

      if (!this.editorInitialized && this.prefillText) {
        this.editor.setText(this.prefillText);
        this.editorInitialized = true;
      }

      this.editorVisible = true;
      this.isEditorEmpty = this.editor.getText().trim().length === 0;

      this.editor.on('text-change', () => {
        this.currentEditorIndex = this.calcIndexWithMentions();
        this.isEditorEmpty = this.editor.getText().trim().length === 0;
      });
      this.editor.on('selection-change', (selection) => {
        if (selection) this.currentEditorIndex = selection.index;
      });
    },
    getMentionsLength() {
      const mentions = this.getMentions();
      let newMentions = [];
      if (mentions.length !== 0) {
        newMentions = mentions.filter((obj) => obj.type !== 'POST');
      }

      return newMentions.length;
    },
    onRemovePoll() {
      this.selectedSharingOption = '';
      this.pollPayload = null;

      this.$emit('onRemovePoll');
    },
    async setMediaForPostUpdate() {
      if (!this.isEdit) return;

      if (this.post?.images.length) {
        await this.getImagePreview(this.post);
      } else if (this.post?.gifUri) {
        this.gifToUpload = {
          id: this.post.gifUri,
          src: this.post.gifUri,
          uri: this.post.gifUri,
        };
      }
    },
    async getImagePreview(post) {
      this.imagesToUpload = [...post.images];

      if (this.imagesToUpload[0]) {
        const res = await this.getPostImageForPreview(
          this.imagesToUpload[0].uri
        );

        const updatedImage = { ...this.imagesToUpload[0], preview: res.data };
        this.imagesToUpload = [updatedImage, ...this.imagesToUpload.slice(1)];
      }
    },
    handleCancelEdit(event) {
      this.$emit('onCancelEdit');
      event.stopPropagation();
    },
    handleSharingOptionClick(key) {
      this.selectedSharingOption = key;
    },
    handlePostButtonState(state) {
      this.postButtonState = state;

      this.$emit('postButtonState', state);
    },
    updatePollList(pollList) {
      this.pollPayload = pollList;
    },
    handleSelectGuide(guide) {
      this.selectedGuide = guide;
      this.guideRemoved = false;
    },
    handleRemoveGuide() {
      this.selectedGuide = null;
      this.selectedSharingOption = '';
      this.guideRemoved = true;
    },
    async handleSelectTicker(ticker) {
      this.selectedSharingOption = '';
      try {
        this.tickerDataLoading = true;
        const response = await this.getSymbolProfileById(ticker.symbolId);

        if (!response) return;
        const symbol = response.data;

        const symbolData = {
          companyId: symbol.companyProfileDetailDto.profileId,
          companyName: symbol.companyProfileDetailDto.displayName,
          avatarUri: symbol.companyProfileDetailDto.avatarUri,
          symbolId: symbol.symbolId,
          name: symbol.name,
          price: symbol.price,
          currencySymbol: symbol.currencySymbol,
          changePercent: symbol.changePercent,
        };

        this.selectedTickers = [...this.selectedTickers, symbolData];
      } catch (error) {
        console.error('Error fetching symbol profile:', error);
      } finally {
        this.tickerDataLoading = false;
      }
    },
    handleRemoveTicker(tickerId) {
      this.selectedTickers = this.selectedTickers.filter(
        (ticker) => ticker.symbolId !== tickerId
      );
    },
    async handleMentionAdd(mention) {
      if (mention.profileType !== 'CompanyProfile') return;

      if (this.selectedTickers.length >= this.tickersInPostLimit) return;

      const alreadyAdded = this.selectedTickers.some(
        (t) => t.companyId === mention.id
      );

      if (alreadyAdded) return;

      try {
        this.tickerDataLoading = true;
        const response = await this.getCompanySymbolsV2({
          companyId: mention.id,
          onlyPrimarySymbol: true,
        });

        if (!response.data[0]) return;

        this.selectedTickers = [...this.selectedTickers, response.data[0]];
      } catch (error) {
        console.error('Error fetching company symbols:', error);
      } finally {
        this.tickerDataLoading = false;
      }
    },
  },
};
